import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Lock, Crown, Check } from "lucide-react";
import { MINI_SITE_TEMPLATES, type MiniSiteTemplateId } from "@/lib/mini-site-templates";
import { getMyMiniSite, updateMiniSite } from "@/lib/mini-site.functions";
import { useIsPremium } from "@/hooks/useIsPremium";
import { TemplateThumb } from "@/components/mini-site/TemplateThumb";

export const Route = createFileRoute("/mini-site/templates/")({
  head: () => ({
    meta: [
      { title: "Templates do Mini Site — Viny Club" },
      { name: "description", content: "Escolha um visual para transformar sua coleção de vinil em um Mini Site compartilhável." },
      { property: "og:title", content: "Templates do Mini Site — Viny Club" },
      { property: "og:description", content: "Escolha um visual para transformar sua coleção de vinil em um Mini Site compartilhável." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: TemplatesCatalogPage,
});

function TemplatesCatalogPage() {
  const { isPremium } = useIsPremium();
  const fetchSite = useServerFn(getMyMiniSite);
  const update = useServerFn(updateMiniSite);
  const qc = useQueryClient();
  const [hovered, setHovered] = useState<string | null>(null);
  const [paywallFor, setPaywallFor] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "free" | "pro">("all");

  const siteQ = useQuery({ queryKey: ["my-mini-site"], queryFn: () => fetchSite() });
  const current = siteQ.data?.template_id;

  const apply = useMutation({
    mutationFn: (template_id: MiniSiteTemplateId) => update({ data: { template_id } }),
    onSuccess: (data) => {
      qc.setQueryData(["my-mini-site"], data);
      toast.success("Template aplicado");
    },
    onError: (e: Error) => {
      if (e.message?.includes("PREMIUM_REQUIRED")) {
        toast.error("Template exclusivo para Embaixadores");
      } else {
        toast.error("Não consegui aplicar");
      }
    },
  });

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-white/10 bg-black/80 px-5 py-4 backdrop-blur sm:px-8">
        <Link
          to="/mini-site"
          className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm text-white/80 hover:bg-white/10 hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" /> Voltar
        </Link>
        <div className="text-sm font-medium">Templates</div>
        <div className="w-24" />
      </header>

      {/* Hero */}
      <section className="px-5 py-16 text-center sm:py-24">
        <h1 className="font-display font-medium" style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)", letterSpacing: "-0.02em" }}>
          Templates
        </h1>
        <p className="mx-auto mt-4 max-w-md text-sm text-white/60 sm:text-base">
          Escolha um ponto de partida, você pode personalizar tudo depois.
        </p>
        <div className="mt-8 inline-flex rounded-full border border-white/15 p-1 text-xs">
          {([
            ["all", "Todos"],
            ["free", "Grátis"],
            ["pro", "Pro"],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={`rounded-full px-4 py-1.5 font-semibold transition ${
                filter === key ? "bg-white text-black" : "text-white/60 hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
        <div className="grid grid-cols-1 gap-x-6 gap-y-16 md:grid-cols-2 lg:grid-cols-3">
          {MINI_SITE_TEMPLATES.filter((t) => filter === "all" || t.tier === filter).map((tpl) => {
            const isPro = tpl.tier === "pro";
            const locked = isPro && !isPremium;
            const isCurrent = current === tpl.id;
            return (
              <Link
                key={tpl.id}
                to="/mini-site/templates/$id"
                params={{ id: tpl.id }}
                className="group block cursor-pointer"
                onMouseEnter={() => setHovered(tpl.id)}
                onMouseLeave={() => setHovered(null)}
              >
                <div
                  className={`relative aspect-[4/3] overflow-hidden rounded-sm bg-neutral-900 transition-all duration-300 ${
                    isCurrent
                      ? "ring-2 ring-white ring-offset-4 ring-offset-black"
                      : "ring-1 ring-white/5 group-hover:ring-white/20"
                  }`}
                >
                  <div className="absolute inset-0 transition-transform duration-700 group-hover:scale-[1.02]">
                    <TemplateThumb tpl={tpl} />
                  </div>

                  {/* Badge */}
                  <div className="absolute left-3 top-3 z-10 flex items-center gap-1.5">
                    {isPro ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-black/80 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white backdrop-blur">
                        <Crown className="h-3 w-3" /> Pro
                      </span>
                    ) : (
                      <span className="inline-flex rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-black">
                        Free
                      </span>
                    )}
                    {tpl.isNew && (
                      <span className="inline-flex rounded-full bg-emerald-400 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-black">
                        Novo
                      </span>
                    )}
                  </div>

                  {isCurrent && (
                    <div className="absolute right-3 top-3 z-10 inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-[10px] font-bold text-black">
                      <Check className="h-3 w-3" /> Em uso
                    </div>
                  )}

                  {/* Hover actions */}
                  <div
                    className={`pointer-events-none absolute inset-0 grid place-items-center bg-black/30 opacity-0 transition-opacity duration-300 ${hovered === tpl.id ? "opacity-100" : ""}`}
                  >
                    <div className="pointer-events-auto flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (locked) {
                            setPaywallFor(tpl.id);
                            return;
                          }
                          apply.mutate(tpl.id);
                        }}
                        className="rounded-full bg-white px-5 py-2 text-xs font-semibold text-black shadow-lg hover:bg-white/90"
                      >
                        {locked ? "Atualizar plano" : "Selecionar"}
                      </button>
                      <span className="rounded-full border border-white/40 bg-black/40 px-5 py-2 text-xs font-semibold text-white backdrop-blur">
                        Preview
                      </span>
                    </div>
                  </div>

                  {locked && (
                    <div className="pointer-events-none absolute right-3 bottom-3 inline-flex items-center gap-1 rounded-full bg-black/70 px-2 py-1 text-[10px] font-semibold text-white backdrop-blur">
                      <Lock className="h-3 w-3" /> Pro
                    </div>
                  )}
                </div>

                <div className="mt-5">
                  <h3 className="flex items-center gap-2 font-display text-3xl font-medium tracking-tight">
                    {tpl.name}
                    {isCurrent && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-black">
                        <Check className="h-3 w-3" /> Em uso
                      </span>
                    )}
                  </h3>
                  <p className="mt-2 text-sm text-white/60">{tpl.description}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Paywall modal */}
      {paywallFor && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/70 px-5 backdrop-blur"
          onClick={() => setPaywallFor(null)}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-white/10 bg-neutral-950 p-8 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-white/10">
              <Crown className="h-5 w-5" />
            </div>
            <h3 className="mt-5 font-display text-2xl font-semibold">Template Pro</h3>
            <p className="mt-2 text-sm text-white/60">
              Este template é exclusivo para Embaixadores. Faça upgrade para desbloquear todos os layouts editoriais.
            </p>
            <div className="mt-6 flex flex-col gap-2">
              <Link
                to="/assinar"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-semibold text-black"
              >
                <Crown className="h-4 w-4" /> Apoie o projeto
              </Link>
              <button
                onClick={() => setPaywallFor(null)}
                className="text-xs text-white/50 hover:text-white"
              >
                Voltar aos templates
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
